import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { getSessionReplay, MEDDPICC_LABELS } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  Minus,
  User,
  MessageSquare,
  Brain,
  Shield,
  Eye,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

const EVIDENCE_LABELS = {
  pain_probe: "Pain Probe",
  metric_present: "Metrics",
  overall_quality: "Quality",
};

const DIM_COLORS = {
  M: "#3b82f6", E: "#8b5cf6", D1: "#f59e0b", D2: "#ef4444",
  P: "#10b981", I: "#ec4899", C1: "#06b6d4", C2: "#6366f1",
};

const TrustBar = ({ trust }) => {
  const color = trust >= 70 ? "bg-emerald-500" : trust >= 45 ? "bg-amber-500" : "bg-red-500";
  return (
    <div className="flex items-center gap-2">
      <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${trust}%` }} />
      </div>
      <span className="text-xs font-mono text-slate-500 w-8 text-right">{trust}</span>
    </div>
  );
};

const MeddpiccMiniChart = ({ data }) => {
  if (!data) return null;
  const dims = ["M", "E", "D1", "D2", "P", "I", "C1", "C2"];
  return (
    <div className="flex gap-1 items-end h-8">
      {dims.map((d) => {
        const val = data[d] || 0;
        return (
          <div key={d} className="flex flex-col items-center gap-0.5">
            <div
              className="w-4 rounded-sm"
              style={{
                height: `${Math.max(2, val * 28)}px`,
                backgroundColor: DIM_COLORS[d],
                opacity: val > 0 ? 1 : 0.2,
              }}
            />
            <span className="text-[8px] text-slate-400">{d}</span>
          </div>
        );
      })}
    </div>
  );
};

const TurnCard = ({ entry, isActive, onClick, isExpanded, onToggle }) => {
  const user_msg = entry.messages.find((m) => m.role === "user");
  const persona_msg = entry.messages.find((m) => m.role === "persona");
  const trustDelta = entry.trust?.delta;
  const dims = entry.meddpicc?.updates || {};

  return (
    <div
      className={`border rounded-lg transition-all cursor-pointer ${
        isActive ? "border-primary/50 bg-primary/[0.02] shadow-sm" : "border-slate-100 hover:border-slate-200"
      }`}
      onClick={onClick}
      data-testid={`replay-turn-${entry.turn}`}
    >
      <div className="px-4 py-3" onClick={(e) => { e.stopPropagation(); onToggle(); }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
              entry.turn === 0 ? "bg-slate-100 text-slate-500" : "bg-primary/10 text-primary"
            }`}>
              {entry.turn}
            </div>

            {/* Trust delta badge */}
            {trustDelta !== undefined && trustDelta !== null && (
              <Badge
                variant="outline"
                className={`text-xs gap-0.5 ${
                  trustDelta > 0 ? "text-emerald-700 border-emerald-200 bg-emerald-50" :
                  trustDelta < 0 ? "text-red-700 border-red-200 bg-red-50" :
                  "text-slate-500 border-slate-200"
                }`}
              >
                {trustDelta > 0 ? <TrendingUp className="w-3 h-3" /> :
                 trustDelta < 0 ? <TrendingDown className="w-3 h-3" /> :
                 <Minus className="w-3 h-3" />}
                {trustDelta > 0 ? "+" : ""}{trustDelta}
              </Badge>
            )}

            {/* MEDDPICC dimension badges */}
            {Object.keys(dims).length > 0 && (
              <div className="flex gap-0.5">
                {Object.entries(dims).map(([dim, val]) => (
                  <Badge
                    key={dim}
                    className="text-[10px] px-1 py-0 font-mono"
                    style={{ backgroundColor: DIM_COLORS[dim] + "22", color: DIM_COLORS[dim], border: `1px solid ${DIM_COLORS[dim]}44` }}
                  >
                    {dim}+{Math.round(val * 100)}%
                  </Badge>
                ))}
              </div>
            )}

            {/* Special events */}
            {entry.phase_advanced && (
              <Badge className="text-[10px] bg-green-100 text-green-800 border-green-200">Phase Advanced</Badge>
            )}
            {entry.plateau_detected && (
              <Badge className="text-[10px] bg-amber-100 text-amber-800 border-amber-200">Plateau</Badge>
            )}
            {entry.persona_switched && (
              <Badge className="text-[10px] bg-purple-100 text-purple-800 border-purple-200">Persona Switched</Badge>
            )}
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400">Trust: {entry.trust_cumulative}</span>
            {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-300" /> : <ChevronDown className="w-4 h-4 text-slate-300" />}
          </div>
        </div>

        {/* Preview text (collapsed) */}
        {!isExpanded && user_msg && (
          <p className="mt-2 text-sm text-slate-500 line-clamp-1 ml-9">{user_msg.content}</p>
        )}
      </div>

      {/* Expanded content */}
      {isExpanded && (
        <div className="border-t border-slate-100 px-4 py-3 space-y-3">
          {/* Evidence signals */}
          {entry.evidence && (
            <div className="bg-slate-50 rounded-md p-3 space-y-2">
              <div className="flex items-center gap-1.5 text-xs font-medium text-slate-600">
                <Eye className="w-3.5 h-3.5" />
                Evidence Extraction
              </div>
              <div className="flex flex-wrap gap-1.5">
                <Badge variant="outline" className="text-xs bg-white">
                  Pain: {entry.evidence.pain_probe}
                </Badge>
                <Badge variant="outline" className="text-xs bg-white">
                  Metrics: {entry.evidence.metric_present ? "Yes" : "No"}
                </Badge>
                <Badge variant="outline" className="text-xs bg-white">
                  Quality: {(entry.evidence.overall_quality * 100).toFixed(0)}%
                </Badge>
                {entry.evidence.negative_signals?.length > 0 && (
                  <Badge variant="outline" className="text-xs bg-red-50 text-red-600 border-red-200">
                    Negatives: {entry.evidence.negative_signals.join(", ")}
                  </Badge>
                )}
              </div>
            </div>
          )}

          {/* Messages */}
          {user_msg && (
            <div className="flex gap-2">
              <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <User className="w-3 h-3 text-blue-600" />
              </div>
              <div className="flex-1">
                <span className="text-xs font-medium text-blue-600">You</span>
                <p className="text-sm text-slate-700 mt-0.5 leading-relaxed">{user_msg.content}</p>
              </div>
            </div>
          )}

          {persona_msg && (
            <div className="flex gap-2">
              <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                <MessageSquare className="w-3 h-3 text-slate-600" />
              </div>
              <div className="flex-1">
                <span className="text-xs font-medium text-slate-600">{persona_msg.persona_name || "Buyer"}</span>
                <p className="text-sm text-slate-700 mt-0.5 leading-relaxed">{persona_msg.content}</p>
              </div>
            </div>
          )}

          {/* Coach */}
          {entry.coach && entry.coach.has_intervention && (
            <div className="flex gap-2 bg-amber-50/50 rounded-md p-2">
              <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Brain className="w-3 h-3 text-amber-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-medium text-amber-700">Coach</span>
                  {entry.coach.prompt_type && (
                    <Badge className="text-[10px] bg-amber-100 text-amber-700 border-amber-200">{entry.coach.prompt_type}</Badge>
                  )}
                </div>
                <p className="text-sm text-amber-800 mt-0.5">{entry.coach.message}</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default function ReplayPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [replayData, setReplayData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTurn, setActiveTurn] = useState(0);
  const [expandedTurns, setExpandedTurns] = useState(new Set([0]));

  useEffect(() => {
    getSessionReplay(sessionId)
      .then((res) => {
        setReplayData(res.data);
        setLoading(false);
        // Auto-expand all turns with events
        const withEvents = new Set(res.data.timeline.filter((t) => t.evidence).map((t) => t.turn));
        setExpandedTurns(withEvents);
      })
      .catch(() => {
        navigate("/");
      });
  }, [sessionId, navigate]);

  const toggleTurn = (turn) => {
    setExpandedTurns((prev) => {
      const next = new Set(prev);
      if (next.has(turn)) next.delete(turn);
      else next.add(turn);
      return next;
    });
  };

  // Trust sparkline data
  const trustTimeline = useMemo(() => {
    if (!replayData) return [];
    return replayData.timeline.map((t) => ({ turn: t.turn, trust: t.trust_cumulative }));
  }, [replayData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-10 h-10 border-3 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const { timeline, meddpicc_timeline, deal_state, session_memory, persona_beliefs } = replayData;
  const lastMeddpicc = meddpicc_timeline[meddpicc_timeline.length - 1] || {};

  return (
    <div className="min-h-screen bg-white" data-testid="replay-page">
      {/* Header */}
      <header className="border-b border-slate-100 bg-white sticky top-0 z-10 accent-border-top">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="gap-1.5 text-slate-500">
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
            <div className="w-px h-5 bg-slate-200" />
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              <h1 className="text-base font-bold text-slate-900">Session Replay</h1>
            </div>
          </div>
          <div className="flex items-center gap-3 text-sm text-slate-500">
            <span>{timeline.length} turns</span>
            <span className="w-px h-4 bg-slate-200" />
            <span>Trust: {deal_state?.trust_score}</span>
            <span className="w-px h-4 bg-slate-200" />
            <span>{deal_state?.phase_name || deal_state?.phase}</span>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Left sidebar — Summary stats */}
          <div className="lg:col-span-1 space-y-4">
            {/* Trust gauge */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">Final Trust</CardTitle>
              </CardHeader>
              <CardContent>
                <TrustBar trust={deal_state?.trust_score || 0} />
              </CardContent>
            </Card>

            {/* MEDDPICC final */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">MEDDPICC Progress</CardTitle>
              </CardHeader>
              <CardContent className="space-y-1.5">
                {["M", "E", "D1", "D2", "P", "I", "C1", "C2"].map((dim) => {
                  const val = lastMeddpicc[dim] || 0;
                  return (
                    <div key={dim} className="flex items-center gap-2">
                      <span className="text-xs font-mono text-slate-500 w-5">{dim}</span>
                      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full"
                          style={{ width: `${val * 100}%`, backgroundColor: DIM_COLORS[dim] }}
                        />
                      </div>
                      <span className="text-[10px] font-mono text-slate-400 w-8 text-right">{(val * 100).toFixed(0)}%</span>
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            {/* Memory */}
            {session_memory && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-slate-600">Session Memory</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-xs text-slate-600">
                  {session_memory.rolling_summary && (
                    <div>
                      <span className="font-medium text-slate-500">Summary:</span>
                      <p className="mt-0.5">{session_memory.rolling_summary}</p>
                    </div>
                  )}
                  {session_memory.discovered_facts?.length > 0 && (
                    <div>
                      <span className="font-medium text-slate-500">Facts:</span>
                      <ul className="mt-0.5 space-y-0.5">
                        {session_memory.discovered_facts.map((f) => <li key={f}>- {f}</li>)}
                      </ul>
                    </div>
                  )}
                  {session_memory.open_loops?.length > 0 && (
                    <div>
                      <span className="font-medium text-slate-500">Open Loops:</span>
                      <ul className="mt-0.5 space-y-0.5">
                        {session_memory.open_loops.map((l) => <li key={l}>- {l}</li>)}
                      </ul>
                    </div>
                  )}
                  {session_memory.commitments_made?.length > 0 && (
                    <div>
                      <span className="font-medium text-slate-500">Commitments:</span>
                      <ul className="mt-0.5 space-y-0.5">
                        {session_memory.commitments_made.map((c) => <li key={c}>- {c}</li>)}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Persona Beliefs */}
            {Object.keys(persona_beliefs || {}).length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-slate-600">Persona Beliefs</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {Object.entries(persona_beliefs).map(([pid, b]) => (
                    <div key={pid} className="space-y-1">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-medium text-slate-700 capitalize">{pid.replace("_", " ")}</span>
                        <Badge variant="outline" className={`text-[10px] ${
                          b.impression === "positive" ? "text-emerald-600 border-emerald-200" :
                          b.impression === "hostile" ? "text-red-600 border-red-200" :
                          b.impression === "skeptical" ? "text-amber-600 border-amber-200" :
                          "text-slate-500 border-slate-200"
                        }`}>
                          {b.impression}
                        </Badge>
                      </div>
                      {b.concerns?.length > 0 && (
                        <div className="text-[10px] text-red-500">
                          Concerns: {b.concerns.join("; ")}
                        </div>
                      )}
                      {b.satisfied_on?.length > 0 && (
                        <div className="text-[10px] text-emerald-500">
                          Satisfied: {b.satisfied_on.join("; ")}
                        </div>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Main timeline */}
          <div className="lg:col-span-3 space-y-4">
            {/* Trust sparkline */}
            <Card>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-slate-500">Trust Timeline</span>
                  <div className="flex items-center gap-3">
                    <MeddpiccMiniChart data={lastMeddpicc} />
                  </div>
                </div>
                <div className="h-10 flex items-end gap-0.5">
                  {trustTimeline.map((point) => {
                    const height = `${Math.max(4, (point.trust / 100) * 40)}px`;
                    const color = point.trust >= 70 ? "#10b981" : point.trust >= 45 ? "#f59e0b" : "#ef4444";
                    return (
                      <div
                        key={`turn-${point.turn}`}
                        className="flex-1 rounded-sm cursor-pointer hover:opacity-80 transition-opacity"
                        style={{ height, backgroundColor: color, minWidth: 3 }}
                        onClick={() => {
                          setActiveTurn(point.turn);
                          setExpandedTurns((s) => new Set([...s, point.turn]));
                        }}
                        title={`Turn ${point.turn}: Trust ${point.trust}`}
                      />
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Navigation */}
            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                size="sm"
                disabled={activeTurn === 0}
                onClick={() => {
                  const prev = Math.max(0, activeTurn - 1);
                  setActiveTurn(prev);
                  setExpandedTurns((s) => new Set([...s, prev]));
                }}
                data-testid="replay-prev-btn"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </Button>
              <span className="text-sm text-slate-500">
                Turn {activeTurn} of {timeline.length - 1}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={activeTurn >= timeline.length - 1}
                onClick={() => {
                  const next = Math.min(timeline.length - 1, activeTurn + 1);
                  setActiveTurn(next);
                  setExpandedTurns((s) => new Set([...s, next]));
                }}
                data-testid="replay-next-btn"
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>

            {/* Turn cards */}
            <div className="space-y-2">
              {timeline.map((entry) => (
                <TurnCard
                  key={entry.turn}
                  entry={entry}
                  isActive={entry.turn === activeTurn}
                  isExpanded={expandedTurns.has(entry.turn)}
                  onClick={() => setActiveTurn(entry.turn)}
                  onToggle={() => toggleTurn(entry.turn)}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
